import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Svg,
  Rect,
} from '@react-pdf/renderer';
import type { ListingSummary } from '../../../types/ListingTypes';

// ── Colour palette ───────────────────────────────────────────────────────────
const C = {
  primary:  '#1e3a5f',
  good:     '#10b981',
  info:     '#2563eb',
  warn:     '#d97706',
  danger:   '#ef4444',
  muted:    '#6b7280',
  border:   '#e5e7eb',
  bg:       '#f8fafc',
  text:     '#1f2937',
  white:    '#ffffff',
} as const;

// ── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 64,        // room for fixed footer
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: C.text,
    backgroundColor: C.white,
  },

  // Header
  header: {
    borderBottomWidth: 2,
    borderBottomColor: C.primary,
    borderBottomStyle: 'solid',
    paddingBottom: 14,
    marginBottom: 22,
  },
  headerBrand: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: C.primary,
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: C.text,
    marginBottom: 5,
  },
  headerMeta: {
    fontSize: 8.5,
    color: C.muted,
    lineHeight: 1.5,
  },

  // Sections
  section: {
    marginBottom: 22,
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: C.primary,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    borderBottomStyle: 'solid',
  },
  subSectionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: C.text,
    marginBottom: 6,
    marginTop: 14,
  },

  // Stat cards
  statsRow: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 14,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: C.border,
    borderStyle: 'solid',
    backgroundColor: C.bg,
  },
  statValue: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 7,
    color: C.muted,
  },

  // Tables
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: C.primary,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 1,
  },
  tableHeaderCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 7.5,
    color: C.white,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 8,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    borderBottomStyle: 'solid',
  },
  tableRowAlt: {
    backgroundColor: C.bg,
  },
  tableCell:     { fontSize: 8.5, color: C.text },
  tableCellGood: { fontSize: 8.5, color: C.good },
  tableCellWarn: { fontSize: 8.5, color: C.warn },
  tableCellBad:  { fontSize: 8.5, color: C.danger },
  tableCellMute: { fontSize: 8.5, color: C.muted },

  // Status badge
  badge: {
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: C.white,
  },

  // Fixed footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    borderTopWidth: 1,
    borderTopColor: C.border,
    borderTopStyle: 'solid',
    paddingTop: 5,
  },
  footerText: {
    fontSize: 7.5,
    color: C.muted,
  },
  emptyText: {
    fontSize: 9,
    color: C.muted,
  },
});

// ── Sub-components ────────────────────────────────────────────────────────────

const PDFRateBar: React.FC<{ rate: number }> = ({ rate }) => {
  const color = rate >= 90 ? C.good : rate >= 70 ? C.info : rate >= 50 ? C.warn : C.danger;
  const filled = Math.min(Math.max(rate, 0), 100) * 0.72; // maps 0–100 → 0–72pt
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Svg width={72} height={7}>
        <Rect x={0} y={0} width={72} height={7} rx={3.5} fill={C.border} />
        <Rect x={0} y={0} width={filled} height={7} rx={3.5} fill={color} />
      </Svg>
      <Text style={{ fontSize: 7.5, color, marginLeft: 4 }}>{rate}%</Text>
    </View>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const bg: Record<string, string> = {
    Open: C.good, Pending: C.warn, Closed: C.muted, Incomplete: C.danger,
  };
  return (
    <View style={[s.badge, { backgroundColor: bg[status] ?? C.muted }]}>
      <Text style={s.badgeText}>{status}</Text>
    </View>
  );
};

const PDFFooter: React.FC<{ agencyName: string; generatedAt: string }> = ({
  agencyName,
  generatedAt,
}) => (
  <View style={s.footer} fixed>
    <Text style={s.footerText}>{agencyName} — RealtyGenie</Text>
    <Text style={s.footerText}>Generated {generatedAt}</Text>
    {/* @ts-ignore render prop is valid in react-pdf */}
    <Text
      style={s.footerText}
      render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
        `Page ${pageNumber} of ${totalPages}`
      }
    />
  </View>
);

// ── Prop types ────────────────────────────────────────────────────────────────

export interface ReportPDFProps {
  agencyName: string;
  generatedAt: string;
  userName: string;
  role: string;
  isManager: boolean;
  overallStats: {
    totalItems: number;
    completedItems: number;
    missingItems: number;
    expiredItems: number;
    completionRate: number;
  };
  categoriesWithStats: Array<{
    id: string;
    name: string;
    stats: {
      totalItems: number;
      completedItems: number;
      missingItems: number;
      expiredItems: number;
      completionRate: number;
    };
  }>;
  agentRows: Array<{
    id?: string;
    name?: string;
    email?: string;
    completionRate?: number;
  }>;
  listings: ListingSummary[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatAddress = (l: ListingSummary): string =>
  [
    l.complexName,
    l.unitNumber ? `Unit ${l.unitNumber}` : null,
    `${l.streetNumber} ${l.streetName}`,
    l.city,
  ]
    .filter(Boolean)
    .join(', ');

// ── Main PDF component ────────────────────────────────────────────────────────

export function ReportPDF(props: ReportPDFProps) {
  const statusCounts = {
    Open:       props.listings.filter(l => l.status === 'Open').length,
    Pending:    props.listings.filter(l => l.status === 'Pending').length,
    Closed:     props.listings.filter(l => l.status === 'Closed').length,
    Incomplete: props.listings.filter(l => l.status === 'Incomplete').length,
  };
  const saleCount   = props.listings.filter(l => l.type === 'Sale').length;
  const rentalCount = props.listings.filter(l => l.type === 'Rental').length;

  const rateColor = (r: number) =>
    r >= 90 ? C.good : r >= 70 ? C.info : r >= 50 ? C.warn : C.danger;

  return (
    <Document
      title={`${props.agencyName} — Compliance & Listings Report`}
      author="RealtyGenie"
      subject="Property compliance and listings data"
    >
      <Page size="A4" style={s.page}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <View style={s.header}>
          <Text style={s.headerBrand}>RealtyGenie</Text>
          <Text style={s.headerTitle}>Compliance &amp; Listings Report</Text>
          <Text style={s.headerMeta}>{props.agencyName}</Text>
          <Text style={s.headerMeta}>Generated: {props.generatedAt}</Text>
          {props.userName ? (
            <Text style={s.headerMeta}>Prepared for: {props.userName} ({props.role})</Text>
          ) : null}
        </View>

        {/* ── Document Compliance ───────────────────────────────────────── */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>Document Compliance</Text>

          {/* Overall stats row */}
          <View style={s.statsRow}>
            {([
              { label: 'Total',      value: String(props.overallStats.totalItems),                                         color: C.info },
              { label: 'Completed',  value: String(props.overallStats.completedItems),                                     color: C.good },
              { label: 'Missing',    value: String(props.overallStats.missingItems),                                       color: C.warn },
              { label: 'Expired',    value: String(props.overallStats.expiredItems),                                       color: C.danger },
              { label: 'Completion', value: `${Math.round(props.overallStats.completionRate)}%`,                           color: rateColor(props.overallStats.completionRate) },
            ] as const).map(stat => (
              <View key={stat.label} style={s.statCard}>
                <Text style={[s.statValue, { color: stat.color }]}>{stat.value}</Text>
                <Text style={s.statLabel}>{stat.label}</Text>
              </View>
            ))}
          </View>

          {/* Category breakdown table */}
          {props.categoriesWithStats.length > 0 ? (
            <View>
              <View style={s.tableHeaderRow}>
                <Text style={[s.tableHeaderCell, { flex: 3 }]}>Category</Text>
                <Text style={[s.tableHeaderCell, { flex: 1 }]}>Total</Text>
                <Text style={[s.tableHeaderCell, { flex: 1 }]}>Done</Text>
                <Text style={[s.tableHeaderCell, { flex: 1 }]}>Missing</Text>
                <Text style={[s.tableHeaderCell, { flex: 1 }]}>Expired</Text>
                <Text style={[s.tableHeaderCell, { flex: 2 }]}>Rate</Text>
              </View>
              {props.categoriesWithStats.map((cat, i) => (
                <View
                  key={cat.id}
                  style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}
                  wrap={false}
                >
                  <Text style={[s.tableCell,     { flex: 3 }]}>{cat.name}</Text>
                  <Text style={[s.tableCell,     { flex: 1 }]}>{cat.stats.totalItems}</Text>
                  <Text style={[s.tableCellGood, { flex: 1 }]}>{cat.stats.completedItems}</Text>
                  <Text style={[s.tableCellWarn, { flex: 1 }]}>{cat.stats.missingItems}</Text>
                  <Text style={[s.tableCellBad,  { flex: 1 }]}>{cat.stats.expiredItems}</Text>
                  <View style={{ flex: 2 }}>
                    <PDFRateBar rate={Math.round(cat.stats.completionRate)} />
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={s.emptyText}>No category data available.</Text>
          )}

          {/* Agent compliance (managers only) */}
          {props.isManager && props.agentRows.length > 0 ? (
            <View>
              <Text style={s.subSectionTitle}>Agent Compliance Breakdown</Text>
              <View style={s.tableHeaderRow}>
                <Text style={[s.tableHeaderCell, { flex: 3 }]}>Agent</Text>
                <Text style={[s.tableHeaderCell, { flex: 3 }]}>Email</Text>
                <Text style={[s.tableHeaderCell, { flex: 2 }]}>Rate</Text>
              </View>
              {props.agentRows.map((agent, i) => {
                const name =
                  agent.name ||
                  'Agent';
                const rate = Math.round(agent.completionRate ?? 0);
                return (
                  <View
                    key={agent.id ?? agent.email ?? i}
                    style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}
                    wrap={false}
                  >
                    <Text style={[s.tableCell,     { flex: 3 }]}>{name}</Text>
                    <Text style={[s.tableCellMute, { flex: 3 }]}>{agent.email ?? '—'}</Text>
                    <View style={{ flex: 2 }}>
                      <PDFRateBar rate={rate} />
                    </View>
                  </View>
                );
              })}
            </View>
          ) : null}
        </View>

        {/* ── Listings ──────────────────────────────────────────────────── */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>
            {props.isManager ? 'Listings (Agency)' : 'Listings (My Listings)'}
          </Text>

          {/* Listings summary stats */}
          <View style={s.statsRow}>
            {([
              { label: 'Total',      value: String(props.listings.length),         color: C.info },
              { label: 'Open',       value: String(statusCounts.Open),       color: C.good },
              { label: 'Pending',    value: String(statusCounts.Pending),    color: C.warn },
              { label: 'Closed',     value: String(statusCounts.Closed),     color: C.muted },
              { label: 'Incomplete', value: String(statusCounts.Incomplete), color: C.danger },
              { label: 'Sales',      value: String(saleCount),               color: C.info },
              { label: 'Rentals',    value: String(rentalCount),             color: C.primary },
            ] as const).map(stat => (
              <View key={stat.label} style={s.statCard}>
                <Text style={[s.statValue, { color: stat.color }]}>{stat.value}</Text>
                <Text style={s.statLabel}>{stat.label}</Text>
              </View>
            ))}
          </View>

          {/* Listings table */}
          {props.listings.length > 0 ? (
            <View>
              <View style={s.tableHeaderRow}>
                <Text style={[s.tableHeaderCell, { flex: 4 }]}>Address</Text>
                <Text style={[s.tableHeaderCell, { flex: 1 }]}>Type</Text>
                <Text style={[s.tableHeaderCell, { flex: 2 }]}>Status</Text>
                {props.isManager ? (
                  <Text style={[s.tableHeaderCell, { flex: 3 }]}>Agent</Text>
                ) : null}
                <Text style={[s.tableHeaderCell, { flex: 2 }]}>Created</Text>
              </View>
              {props.listings.map((l, i) => (
                <View
                  key={l.id}
                  style={[s.tableRow, i % 2 === 1 ? s.tableRowAlt : {}]}
                  wrap={false}
                >
                  <Text style={[s.tableCell, { flex: 4 }]}>{formatAddress(l)}</Text>
                  <Text style={[s.tableCell, { flex: 1 }]}>{l.type}</Text>
                  <View style={{ flex: 2 }}>
                    <StatusBadge status={l.status} />
                  </View>
                  {props.isManager ? (
                    <Text style={[s.tableCellMute, { flex: 3 }]}>
                      {l.agent ? `${l.agent.fullNames} ${l.agent.lastName}` : '—'}
                    </Text>
                  ) : null}
                  <Text style={[s.tableCellMute, { flex: 2 }]}>
                    {new Date(l.createdAt).toLocaleDateString('en-ZA')}
                  </Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={s.emptyText}>No listings found.</Text>
          )}
        </View>

        {/* Fixed footer on every page */}
        <PDFFooter agencyName={props.agencyName} generatedAt={props.generatedAt} />

      </Page>
    </Document>
  );
};
